import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header, Footer } from '../../components';
import storage from '../../utils/storage';
import { formatDate, getReadingTime } from '../../utils/date';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';
import './PostDetail.css';

// 初始化Markdown解析器
const md = new MarkdownIt({
  highlight: function (str, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return '<pre class="hljs"><code>' +
               hljs.highlight(str, { language: lang }).value +
               '</code></pre>';
      } catch (__) {}
    }
    return '<pre class="hljs"><code>' + md.utils.escapeHtml(str) + '</code></pre>';
  }
});

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadPost();
    const user = storage.getCurrentUser();
    setCurrentUser(user);
  }, [postId]);

  const loadPost = () => {
    try {
      const posts = storage.getPosts();
      const postData = posts.find(p => p.id === postId);
      
      if (postData) {
        // 增加阅读次数
        postData.views = (postData.views || 0) + 1;
        storage.savePosts(posts);
        
        setPost(postData);
        
        // 获取作者信息
        const users = storage.getUsers();
        const authorData = users.find(u => u.id === postData.author);
        setAuthor(authorData);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading post:', error);
      setLoading(false);
    }
  };

  const handleLike = () => {
    if (!post) return;
    
    const posts = storage.getPosts();
    const postIndex = posts.findIndex(p => p.id === postId);
    
    if (postIndex !== -1) {
      posts[postIndex].likes = (posts[postIndex].likes || 0) + 1;
      storage.savePosts(posts);
      setPost(posts[postIndex]);
    }
  };

  const handleDelete = () => {
    if (!window.confirm('确定要删除这篇文章吗？此操作不可恢复。')) {
      return;
    }
    
    try {
      const posts = storage.getPosts();
      const filteredPosts = posts.filter(p => p.id !== postId);
      storage.savePosts(filteredPosts);
      
      alert('文章已删除');
      navigate('/');
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('删除失败，请稍后重试');
    }
  };

  const renderMarkdown = (content) => {
    return {
      __html: md.render(content)
    };
  };

  if (loading) {
    return (
      <div className="app">
        <Header />
        <main className="main">
          <div className="container-sm">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>加载中...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="app">
        <Header />
        <main className="main">
          <div className="container-sm">
            <div className="not-found">
              <h1>文章不存在</h1>
              <p>您访问的文章可能已被删除或不存在</p>
              <Link to="/" className="back-link">
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Header />
      <main className="main">
        <article className="post-detail">
          {/* 文章头部 */}
          <header className="post-header">
            <div className="container-sm">
              <div className="post-meta">
                {author && (
                  <div className="post-author">
                    <img 
                      src={author.avatar} 
                      alt={author.username}
                      className="author-avatar"
                    />
                    <div className="author-info">
                      <span className="author-name">{author.username}</span>
                      <span className="post-date">
                        发布于 {formatDate(post.createdAt, 'YYYY年MM月DD日 HH:mm')}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="post-stats">
                  <span className="stat-item">
                    👁 {post.views || 0} 阅读
                  </span>
                  <span className="stat-item">
                    ⏱️ {getReadingTime(post.content)} 分钟
                  </span>
                </div>
              </div>

              <h1 className="post-title">{post.title}</h1>

              {post.tags && post.tags.length > 0 && (
                <div className="post-tags">
                  {post.tags.map((tag, index) => (
                    <span key={index} className="post-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* 操作按钮 */}
              {currentUser && currentUser.id === post.author && (
                <div className="post-actions">
                  <button 
                    onClick={() => navigate(`/write?edit=${post.id}`)}
                    className="btn btn-edit"
                  >
                    编辑文章
                  </button>
                  <button 
                    onClick={handleDelete}
                    className="btn btn-delete"
                  >
                    删除文章
                  </button>
                </div>
              )}
            </div>
          </header>

          {/* 文章内容 */}
          <div className="post-content">
            <div className="container-sm">
              <div 
                className="markdown-content"
                dangerouslySetInnerHTML={renderMarkdown(post.content)}
              />
            </div>
          </div>

          {/* 文章底部 */}
          <footer className="post-footer">
            <div className="container-sm">
              <div className="post-actions-bar">
                <button 
                  onClick={handleLike}
                  className="like-btn"
                >
                  ❤️ {post.likes || 0}
                </button>
                
                <div className="post-share">
                  <span>分享文章:</span>
                  <button className="share-btn">📋 复制链接</button>
                </div>
              </div>

              {post.updatedAt !== post.createdAt && (
                <div className="post-updated">
                  <p>
                    最后更新于 {formatDate(post.updatedAt, 'YYYY年MM月DD日 HH:mm')}
                  </p>
                </div>
              )}
            </div>
          </footer>
        </article>

        {/* 相关文章 */}
        <div className="related-posts">
          <div className="container-sm">
            <h3>相关文章</h3>
            {/* 这里可以添加相关文章的逻辑 */}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PostDetail;